import { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { postApi } from "../../api/getUser";
import { fetchServicesTasks, fetchServiceTaskCategories } from "../../features/servicesTasks/servicesTasksSlice";

export const useServiceTaskCategory = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.user);
  const allServiceTaskCategories = useSelector(
    (state) => state.servicesTasks.allServiceTaskCategories,
  );

  // State
  const [showServiceTaskCategoryPopup, setShowServiceTaskCategoryPopup] =
    useState(false);
  const [tempServiceTaskCategoryData, setTempServiceTaskCategoryData] =
    useState(null);
  const [serviceTaskCategoryPopupErr, setServiceTaskCategoryPopupErr] =
    useState("");
  const [isServiceTaskCategorySaving, setIsServiceTaskCategorySaving] =
    useState(false);
  const [isServiceTaskCategoryDeleting, setIsServiceTaskCategoryDeleting] =
    useState(false);

  // Handler for creating new Service Task Category
  const handleCreateServiceTaskCategory = (data) => {
    const tempData = {
      label: data,
      value: `${userId}_${Date.now()}`,
      __isNew__: true,
    };

    setTempServiceTaskCategoryData(tempData);
    setShowServiceTaskCategoryPopup(true);
  };

  // Handler for saving service task category from popup
  const handleSaveServiceTaskCategory = async ({
    categoryName,
    categoryId,
    isNew,
    setValue,
    watch,
  }) => {
    // Duplicate name check
    const existingCategories = allServiceTaskCategories.filter(
      (c) =>
        c.label?.toLowerCase() === categoryName?.toLowerCase() &&
        c.value !== categoryId,
    );
    if (existingCategories.length > 0) {
      setServiceTaskCategoryPopupErr(t("dup_service_task_category_err"));
      return;
    }

    setIsServiceTaskCategorySaving(true);
    setServiceTaskCategoryPopupErr("");

    try {
      const createApiJson = {
        querystring: "addEditServiceTaskCategory",
        category_name: categoryName,
        action: isNew ? 1 : 2,
        operation: isNew ? "create" : "update",
        st_category_id: categoryId,
      };
      const response = await postApi(createApiJson, "workOrder");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.action_value === "record_exists") {
          errMsg = t("dup_service_task_category_err");
        }
        setServiceTaskCategoryPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        if (isNew) {
          setValue("serviceTaskCategory", {
            label: categoryName,
            value: categoryId,
          });
        } else {
          const currentValue = watch("serviceTaskCategory");
          if (currentValue?.value === tempServiceTaskCategoryData.value) {
            setValue("serviceTaskCategory", {
              label: categoryName,
              value: categoryId,
            });
          }
        }

        dispatch(fetchServiceTaskCategories({ force: true }));
        setShowServiceTaskCategoryPopup(false);
      }
    } catch (error) {
      console.error("Error saving service task category:", error);
      setServiceTaskCategoryPopupErr(t("form_err_msg"));
    } finally {
      setIsServiceTaskCategorySaving(false);
    }
  };

  // Handler for deleting service task category from popup
  const handleDeleteServiceTaskCategory = async (
    categoryId,
    setValue,
    watch,
  ) => {
    setIsServiceTaskCategoryDeleting(true);
    setServiceTaskCategoryPopupErr("");

    try {
      const deleteApiData = {
        delete_ids: categoryId,
        querystring: "multiple_delete",
        sub_query_string: "SERVICES_TASK_CATEGORIES",
      };

      const response = await postApi(deleteApiData, "deleteData");

      if (!response?.success) {
        let errMsg = t("form_err_msg");
        if (response?.delete_result?.length > 0) {
          errMsg = t("delete_service_task_category_err");
        }
        setServiceTaskCategoryPopupErr(errMsg);
        return;
      }

      if (response?.success) {
        const currentValue = watch("serviceTaskCategory");
        if (currentValue?.value === tempServiceTaskCategoryData.value) {
          setValue("serviceTaskCategory", null);
        }

        dispatch(fetchServiceTaskCategories({ force: true }));

        //Fetch all service tasks
        (async () => {
          const apiData = {
            //email: "testgroup@12.in",
            querystring: "getallservicetask",
            service_veh_id: "All",
          };
          dispatch(fetchServicesTasks(apiData));
          // }
        })();

        setShowServiceTaskCategoryPopup(false);
      }
    } catch (error) {
      console.error("Error deleting service task category:", error);
      setServiceTaskCategoryPopupErr(t("form_err_msg"));
    } finally {
      setIsServiceTaskCategoryDeleting(false);
    }
  };

  // Handler for closing popup and clearing errors
  const handleCloseServiceTaskCategoryPopup = () => {
    setServiceTaskCategoryPopupErr("");
  };

  return {
    // State
    showServiceTaskCategoryPopup,
    setShowServiceTaskCategoryPopup,
    tempServiceTaskCategoryData,
    setTempServiceTaskCategoryData,
    serviceTaskCategoryPopupErr,
    isServiceTaskCategorySaving,
    isServiceTaskCategoryDeleting,

    // Handlers
    handleCreateServiceTaskCategory,
    handleSaveServiceTaskCategory,
    handleDeleteServiceTaskCategory,
    handleCloseServiceTaskCategoryPopup,
  };
};
